/** Alles wat de browser aan de server vraagt, loopt hierlangs. */

import type { Aanspreekvorm, Sfeer } from '@tolk/shared';

export interface KamerPubliek {
  id: string;
  label: string;
  pinVereist: boolean;
  langA: string;
  langB: string;
  aanspreekvorm: Aanspreekvorm;
  sfeer: Sfeer;
  stemDelen: boolean;
  correctie: boolean;
  /** Typen en lezen, geen spraak en geen microfoon. */
  alleenChat: boolean;
  /** Stilte na het laatste woord, in ms. Zie shared/src/pauze.ts. */
  pauzeMs: number;
  /** Google Meet-link van het gesprek, of null. */
  meetLink: string | null;
  expiresAt: number;
}

export interface NieuweKamerAntwoord extends KamerPubliek {
  link: string;
  pin: string | null;
  debug: boolean;
  budgetEur: number;
}

/** Een foutcode van de server, met een Nederlandse melding erbij. */
export class ApiFout extends Error {
  constructor(
    readonly code: string,
    bericht: string,
    readonly status: number,
  ) {
    super(bericht);
    this.name = 'ApiFout';
  }
}

const MELDINGEN: Record<string, string> = {
  room_not_found: 'Deze kamer bestaat niet. Controleer de link.',
  room_expired: 'Deze kamer is verlopen of gesloten.',
  taal_onbekend: 'Die taal kent de app niet.',
  zelfde_taal: 'Beide sprekers dezelfde taal geven, is geen tolkgesprek.',
  niet_ingelogd: 'Je bent niet (meer) ingelogd. Log opnieuw in.',
  inloggen_mislukt: 'E-mailadres of toegangscode klopt niet.',
  inloggen_onvolledig: 'Vul allebei de velden in.',
  te_veel_pogingen: 'Te veel pogingen. Wacht even en probeer het opnieuw.',
  netwerk: 'Geen verbinding. Controleer je internet.',
  woord_leeg: 'Vul allebei de velden in.',
  woord_gelijk: 'Van en naar zijn hetzelfde. Dan valt er niets te corrigeren.',
  woord_bestaat: 'Dat woord staat al in de lijst.',
  woord_onbekend: 'Dat woord bestaat niet meer.',
  server_onbereikbaar: 'De server reageert niet. Probeer het zo nog eens.',
  meet_link_ongeldig:
    'Dat is geen Google Meet-link. Plak de link uit Meet zelf, bijvoorbeeld https://meet.google.com/abc-defg-hij.',
  budget_ongeldig: 'Kies een bedrag boven nul.',
  budget_te_hoog: 'Hoger dan het maandbudget kan niet.',
  verlooptijd_ongeldig: 'Dat tijdstip ligt al achter ons.',
  verlooptijd_te_ver: 'Langer dan zeven dagen vanaf nu kan niet.',
};

/**
 * Fouten waarbij het zin heeft om het opnieuw te proberen. Het verschil is niet
 * cosmetisch: bij een verlopen kamer helpt opnieuw proberen NOOIT, bij een
 * server die even weg is bijna altijd.
 */
const OPNIEUW_PROBEREN = new Set(['netwerk', 'server_onbereikbaar']);

export function magOpnieuw(code: string): boolean {
  return OPNIEUW_PROBEREN.has(code);
}

export function meldingVoor(code: string): string {
  return MELDINGEN[code] ?? 'Er ging iets mis bij de server.';
}

async function haal<T>(pad: string, opties?: RequestInit): Promise<T> {
  let antwoord: Response;
  try {
    antwoord = await fetch(pad, {
      ...opties,
      headers: { 'content-type': 'application/json', ...opties?.headers },
    });
  } catch {
    // Een netwerkfout GOOIT bij fetch, hij geeft geen antwoord terug. Zonder
    // deze vangst klapt de pagina in plaats van een melding te tonen.
    throw new ApiFout('netwerk', MELDINGEN['netwerk'] ?? 'Geen verbinding.', 0);
  }

  if (!antwoord.ok) {
    // 5xx komt van de server zelf of van de proxy ervoor, en die laatste stuurt
    // geen JSON. Dat is iets anders dan "deze kamer bestaat niet" en hoort dus
    // niet dezelfde melding te geven.
    // 401 ZONDER JSON KOMT VAN NGINX, NIET VAN DE APP. Zolang het
    // htpasswd-venster voor /api/beheer/ staat, geeft de proxy een 401 met
    // HTML erin. Die viel hiervoor in de bak 'onbekend' en gaf de melding "Er
    // ging iets mis bij de server" - waar of niet, het zegt niet wat je moet
    // doen. Een 401 is altijd hetzelfde probleem: je bent niet ingelogd.
    let code =
      antwoord.status >= 500
        ? 'server_onbereikbaar'
        : antwoord.status === 401
          ? 'niet_ingelogd'
          : 'onbekend';
    try {
      const body = (await antwoord.json()) as { error?: string };
      if (typeof body.error === 'string') code = body.error;
    } catch {
      // Geen JSON terug. De code uit de status blijft staan.
    }
    throw new ApiFout(code, meldingVoor(code), antwoord.status);
  }

  return (await antwoord.json()) as T;
}

export function leesKamer(id: string): Promise<KamerPubliek> {
  return haal<KamerPubliek>(`/api/rooms/${encodeURIComponent(id)}`);
}

export interface KamerAanvraag {
  label?: string;
  langA: string;
  langB: string;
  aanspreekvorm?: Aanspreekvorm;
  sfeer?: Sfeer;
  stemDelen?: boolean;
  correctie?: boolean;
  alleenChat?: boolean;
  metPin?: boolean;
  debug?: boolean;
  ttlUren?: number;
  /** Geen einddatum; ttlUren telt dan niet. Zie shared/src/vast.ts. */
  vast?: boolean;
  budgetEur?: number;
  meetLink?: string;
}

export function maakKamer(aanvraag: KamerAanvraag): Promise<NieuweKamerAntwoord> {
  return haal<NieuweKamerAntwoord>('/api/beheer/rooms', {
    method: 'POST',
    body: JSON.stringify(aanvraag),
  });
}

/**
 * Een gesprek dat nog loopt, zoals de beheerlijst hem geeft.
 *
 * GEEN PINCODE, en dat is geen vergetelheid van de server maar de reden dat
 * dit een eigen type is. De pin bestaat alleen op het moment dat je de kamer
 * aanmaakt; daarna is alleen de hash bewaard. Wie hem kwijt is, maakt een
 * nieuwe kamer.
 */
export type KamerInLijst = Omit<NieuweKamerAntwoord, 'pin'>;

/**
 * De gesprekken die nu nog lopen. Alleen voor de beheerder.
 *
 * DIT KOMT VAN DE SERVER EN NIET UIT localStorage, met opzet. Het briefje in
 * lib/mijnKamers.ts kent alleen de kamers die op DIT toestel zijn aangemaakt.
 * Maak je een kamer op je Mac en zoek je hem op je telefoon, dan helpt dat
 * briefje je niet - en dat is precies wanneer je de lijst nodig hebt.
 *
 * Nieuwste eerst, en verlopen of gesloten gesprekken staan er niet bij: die
 * kun je toch niet meer binnenlopen.
 */
export function leesKamers(): Promise<{ kamers: KamerInLijst[] }> {
  return haal<{ kamers: KamerInLijst[] }>('/api/beheer/rooms');
}

/**
 * Een gesprek sluiten. DIT KAN NIET TERUG.
 *
 * De server zet `closed_at` en er is geen route die dat ongedaan maakt. Daarna
 * is de kamer weg uit de lijst en geeft de link 404 - ook voor je gast, ook als
 * die op dat moment binnen zit. De rij en de berichten blijven nog in de
 * database staan tot de opruimer ze weghaalt, maar niemand kan er nog bij.
 *
 * Vraag het dus altijd eerst. Zie components/Kamerlijst.tsx voor de
 * bevestiging die daarvoor staat.
 */
export function sluitKamer(kamerId: string): Promise<{ gesloten: boolean }> {
  return haal<{ gesloten: boolean }>(
    `/api/beheer/rooms/${encodeURIComponent(kamerId)}/close`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/**
 * De kostenstand van een kamer. Alleen voor de beheerder.
 *
 * Bedragen in EURO en niet in dollar: de server rekent om, zodat er maar een
 * plek is waar de koers staat.
 */
export interface KostenStand {
  kamerEur: number;
  dagEur: number;
  maandEur: number;
  kamerPlafondEur: number;
  dagPlafondEur: number;
  maandPlafondEur: number;
  waarschuwing: 'maand' | 'kamer' | 'dag' | null;
}

/**
 * Budget, verlooptijd of Meet-link van een LOPEND gesprek bijstellen. Zelfde
 * kamer, zelfde link. Alles wat je niet meegeeft, blijft staan; `meetLink: null`
 * haalt de link weg.
 *
 * ABSOLUTE WAARDEN: de knop "+ EUR 1" rekent zelf het nieuwe totaal uit. Een
 * dubbele tik zet dan twee keer hetzelfde bedrag, in plaats van ongemerkt twee
 * euro erbij.
 */
export function wijzigKamer(
  kamerId: string,
  wijziging: {
    budgetEur?: number;
    expiresAt?: number;
    vast?: boolean;
    meetLink?: string | null;
    pauzeMs?: number;
  },
): Promise<Omit<NieuweKamerAntwoord, 'pin'>> {
  // Zonder pin: die gaat alleen bij het AANMAKEN een keer mee.
  return haal<Omit<NieuweKamerAntwoord, 'pin'>>(`/api/beheer/rooms/${encodeURIComponent(kamerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(wijziging),
  });
}

/**
 * Kan mislukken met 503 zodra de beheerpoort in productie dichtstaat, en met
 * 404 als de kamer verlopen is. De aanroeper hoort dat op te vangen en de balk
 * dan gewoon niet te tonen - een kostenbalk is nooit belangrijk genoeg om een
 * gesprek voor te onderbreken.
 */
export function leesKosten(kamerId: string): Promise<KostenStand> {
  return haal<KostenStand>(`/api/beheer/rooms/${encodeURIComponent(kamerId)}/kosten`);
}

/** Een regel in het gespreksverslag. */
export interface VerslagBericht {
  id: string;
  senderSlot: 'a' | 'b';
  originalText: string;
  /** null als de vertaling mislukte of nog liep. Zie `status`. */
  translatedText: string | null;
  srcLang: string;
  dstLang: string;
  status: 'pending' | 'translated' | 'failed';
  createdAt: number;
  ingesproken: boolean;
}

export interface Verslag {
  kamer: {
    id: string;
    label: string;
    langA: string;
    langB: string;
    /** Stond de spellingcorrectie aan? Dan is "origineel" verbeterde tekst. */
    correctie: boolean;
    createdAt: number;
    expiresAt: number;
    closedAt: number | null;
  };
  berichten: VerslagBericht[];
  gemaaktOp: number;
  /** Vanaf dit moment kan de opruimer het gesprek wissen. */
  gewistVanaf: number;
}

/**
 * Alles voor het gespreksverslag. Alleen voor de beheerder.
 *
 * Werkt ook voor een gesprek dat gesloten of verlopen is, zolang het nog niet
 * gewist is. Daarna 404, met de melding room_not_found.
 */
export function leesVerslag(kamerId: string): Promise<Verslag> {
  return haal<Verslag>(`/api/beheer/rooms/${encodeURIComponent(kamerId)}/verslag`);
}

/** Het adres van de verslagpagina. Een pagina en geen download: de browser maakt de PDF. */
export function verslagPad(kamerId: string): string {
  return `/verslag/${encodeURIComponent(kamerId)}`;
}

export interface Woord {
  id: number;
  van: string;
  naar: string;
  globaal: boolean;
}

export async function leesWoorden(kamerId: string): Promise<Woord[]> {
  const antwoord = await haal<{ woorden: Woord[] }>(
    `/api/rooms/${encodeURIComponent(kamerId)}/woorden`,
  );
  return antwoord.woorden;
}

export function maakWoord(
  kamerId: string,
  woord: { van: string; naar: string; globaal: boolean },
): Promise<Woord> {
  return haal<Woord>(`/api/rooms/${encodeURIComponent(kamerId)}/woorden`, {
    method: 'POST',
    body: JSON.stringify(woord),
  });
}

export function verwijderWoord(kamerId: string, id: number): Promise<{ verwijderd: boolean }> {
  return haal<{ verwijderd: boolean }>(
    `/api/rooms/${encodeURIComponent(kamerId)}/woorden/${id}`,
    { method: 'DELETE' },
  );
}

export function qrPad(id: string): string {
  return `/api/rooms/${encodeURIComponent(id)}/qr.svg`;
}

/**
 * Inloggen als beheerder.
 *
 * `credentials` staat niet expliciet aan: fetch stuurt cookies van dezelfde
 * herkomst standaard mee, en de client wordt door dezelfde server geserveerd.
 */
export function inloggen(email: string, geheim: string): Promise<{ email: string }> {
  return haal<{ email: string }>('/api/toegang/login', {
    method: 'POST',
    body: JSON.stringify({ email, geheim }),
  });
}

export function uitloggen(): Promise<{ ok: boolean }> {
  return haal<{ ok: boolean }>('/api/toegang/logout', { method: 'POST' });
}

/**
 * De noodrem: elk ander apparaat waarop je ingelogd bent, staat hierna buiten.
 *
 * DIT APPARAAT NIET - de server geeft een vers cookie mee. Daarom hoeft de
 * client hierna niet naar het inlogscherm. Zie routes/toegang.ts op de server.
 */
export function logUitOpAlleApparaten(): Promise<{ ok: boolean }> {
  return haal<{ ok: boolean }>('/api/beheer/sessies/uitloggen-overal', { method: 'POST' });
}

/** Wie ben ik? Gooit een ApiFout met code `niet_ingelogd` als je buiten staat. */
export function wieBenIk(): Promise<{ email: string }> {
  return haal<{ email: string }>('/api/toegang/ik');
}
